import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import netlifyIdentity from "../identity";

const WIDGET_SRC = "https://widget.cloudinary.com/v2.0/global/all.js";
const CLOUD_NAME = "dbgzzcwvr";
const UPLOAD_PRESET = "nb-site-media"; // unsigned preset
const TAG = "nb-gallery";

export default function AdminUpload({ setMedia }) {
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const widgetRef = useRef(null);
  const bcRef = useRef(null);

  // auth: "checking" | "allowed" | "denied"
  const [auth, setAuth] = useState("checking");
  const [me, setMe] = useState(null);

  /* -------- AUTH GUARD (no flicker) -------- */
  useEffect(() => {
    const isAdmin = (user) => {
      const roles = user?.app_metadata?.roles || [];
      return !!user && roles.includes("admin");
    };

    const current = netlifyIdentity.currentUser();
    if (isAdmin(current)) {
      setMe(current);
      setAuth("allowed");
    } else {
      const handleInitOrLogin = (user) => {
        if (isAdmin(user)) {
          setMe(user);
          setAuth("allowed");
        } else {
          setAuth("denied");
          navigate("/login", { replace: true });
        }
      };

      const handleLogout = () => {
        setMe(null);
        setAuth("denied");
        navigate("/login", { replace: true });
      };

      netlifyIdentity.on("init", handleInitOrLogin);
      netlifyIdentity.on("login", handleInitOrLogin);
      netlifyIdentity.on("logout", handleLogout);

      setTimeout(() => {
        const u = netlifyIdentity.currentUser();
        if (isAdmin(u)) {
          setMe(u);
          setAuth("allowed");
        } else if (!u) {
          setAuth("denied");
          navigate("/login", { replace: true });
        }
      }, 0);

      return () => {
        netlifyIdentity.off("init", handleInitOrLogin);
        netlifyIdentity.off("login", handleInitOrLogin);
        netlifyIdentity.off("logout", handleLogout);
      };
    }
  }, [navigate]);

  /* -------- Broadcast Channel -------- */
  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel("nb-media");
    } catch {
      bcRef.current = null;
    }
    return () => {
      try {
        bcRef.current?.close();
      } catch {}
    };
  }, []);

  /* -------- Load Cloudinary Widget Script -------- */
  const ensureCloudinaryWidget = () =>
    new Promise((resolve) => {
      if (window.cloudinary?.createUploadWidget) return resolve();
      const existing = document.querySelector(`script[src="${WIDGET_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        return;
      }
      const s = document.createElement("script");
      s.src = WIDGET_SRC;
      s.async = true;
      s.onload = () => resolve();
      document.body.appendChild(s);
    });

  /* -------- Init Widget (after auth passes) -------- */
  useEffect(() => {
    if (auth !== "allowed") return;
    let removeClick;

    const init = async () => {
      await ensureCloudinaryWidget();
      if (!window.cloudinary?.createUploadWidget) return;

      widgetRef.current = window.cloudinary.createUploadWidget(
        {
          cloudName: CLOUD_NAME,
          uploadPreset: UPLOAD_PRESET,
          sources: ["local", "url", "camera"],
          multiple: true,
          folder: "nb-website-uploads",
          resourceType: "auto", // image + video
          tags: [TAG],
          maxFileSize: 15 * 1024 * 1024,
          clientAllowedFormats: ["jpg", "jpeg", "png", "webp", "mp4", "mov"],
        },
        (error, result) => {
          if (error) {
            console.error("Cloudinary widget error:", error);
            return;
          }
          if (result?.event === "success") {
            // 1) Broadcast to open tabs
            try {
              bcRef.current?.postMessage({ type: "refresh" });
            } catch {}
            // 2) localStorage bump → 'storage' event across tabs
            try {
              localStorage.setItem("nb_media_bump", String(Date.now()));
            } catch {}
            // 3) Second nudge after indexing delay
            setTimeout(() => {
              try {
                bcRef.current?.postMessage({ type: "refresh" });
              } catch {}
              try {
                localStorage.setItem("nb_media_bump", String(Date.now()));
              } catch {}
            }, 2000);
          }
        }
      );

      const open = () => widgetRef.current && widgetRef.current.open();
      const el = btnRef.current;
      el?.addEventListener("click", open);
      removeClick = () => el?.removeEventListener("click", open);
    };

    init();
    return () => removeClick?.();
  }, [auth]);

  /* ---------------- UI ---------------- */
  const keyframes = `
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;

  if (auth === "checking") {
    return (
      <div style={styles.full}>
        <style>{keyframes}</style>
        <div style={styles.loaderWrap}>
          <div style={styles.spinner} />
          <div style={styles.loaderText}>Verifying admin access…</div>
        </div>
      </div>
    );
  }

  if (auth !== "allowed") return null;

  return (
    <div style={styles.page}>
      <style>{keyframes}</style>
      <div style={styles.shell}>
        <header style={styles.header}>
          <div style={styles.brandLeft}>
            <div style={styles.logo}>🔰</div>
            <div>
              <h1 style={styles.title}>Admin Console</h1>
              <p style={styles.subtitle}>Upload to Gallery</p>
            </div>
          </div>

          <div style={styles.actions}>
            <button
              className="btn"
              onClick={() => netlifyIdentity.open("account")}
            >
              Account
            </button>
            <button
              className="btn"
              onClick={() => {
                netlifyIdentity.logout();
                navigate("/login", { replace: true }); // instant fallback
              }}
            >
              Logout
            </button>
          </div>
        </header>

        <main style={styles.card}>
          <div style={styles.heroIcon}>📤</div>
          <h2 style={{ margin: "4px 0 6px" }}>One-click Cloud Upload</h2>
          <p style={styles.desc}>
            Select images or videos. We’ll auto-optimize and tag them as{" "}
            <code>{TAG}</code>. Your public gallery updates instantly across
            open tabs.
          </p>

          <button ref={btnRef} className="btn btn-primary" style={styles.cta}>
            Upload to Gallery
          </button>

          {me?.email && (
            <div style={styles.signedIn}>
              Signed in as <strong>{me.email}</strong>
            </div>
          )}

          <div style={styles.tips}>
            <span>Note:</span> Large files or unsupported formats will show an
            in-widget error.
          </div>
        </main>

        <footer style={styles.footer}>
          <a href="/" style={{ textDecoration: "underline" }}>
            ← Back to site
          </a>
        </footer>
      </div>
    </div>
  );
}

const styles = {
  full: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background:
      "radial-gradient(1400px 600px at 10% -10%, #eef4ff, transparent), radial-gradient(1400px 600px at 110% 110%, #f7faff, transparent)",
  },
  loaderWrap: { display: "grid", placeItems: "center", gap: 10 },
  spinner: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "3px solid #cfd7ff",
    borderTopColor: "#5b77ff",
    animation: "spin 1s linear infinite",
  },
  loaderText: { color: "#555", fontSize: 14 },

  page: {
    minHeight: "100vh",
    background:
      "conic-gradient(from 140deg at 20% 10%, #eef4ff, #fdfcff, #eef4ff)",
    display: "grid",
    placeItems: "center",
    padding: 24,
  },
  shell: { width: "100%", maxWidth: 960 },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
    marginBottom: 14,
  },
  brandLeft: { display: "flex", alignItems: "center", gap: 10 },
  logo: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "#eef3ff",
    display: "grid",
    placeItems: "center",
    fontSize: 20,
  },
  title: { margin: 0, fontSize: 22 },
  subtitle: { margin: 0, color: "#6d6d6d" },

  card: {
    border: "1px solid rgba(190,200,255,0.5)",
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(10px)",
    borderRadius: 20,
    boxShadow: "0 30px 80px rgba(60,80,160,0.18)",
    padding: 22,
    textAlign: "center",
  },
  heroIcon: { fontSize: 40, marginBottom: 6 },
  desc: { color: "#4a4a4a", margin: "0 0 12px" },
  cta: { padding: "12px 20px", fontWeight: 700 },

  signedIn: {
    marginTop: 12,
    display: "inline-block",
    background: "#f5f7ff",
    border: "1px solid #e5e9ff",
    padding: "8px 12px",
    borderRadius: 10,
    fontSize: 13,
  },
  tips: { fontSize: 12, color: "#6f6f6f", marginTop: 10 },

  actions: { display: "flex", gap: 8 },
  footer: { textAlign: "center", marginTop: 14 },
};
